/**
 * Filter sidebar section for the StatelessWorkCardsUpsellModal.
 *
 * Handles the accordion collapse/expand on mobile, wraps the
 * FilterPanel with an ImpressionWatcher, and renders the
 * FilterToggle control.
 */

import React from 'react';

import {
  BuildPillTrackClick,
  DateRangeFilter,
} from 'js/bulk_download/work_cards_shared/types';
import { WorkId } from 'js/liswp/types';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { ImpressionEntity } from 'js/utils/impressions/watchImpressions';
import { FilterPanel } from 'js/bulk_download/work_cards_shared/FilterPanel';
import { FilterToggle } from 'js/bulk_download/work_cards_shared/FilterToggle';

import {
  FiltersColumn,
  MobileAccordionDivider,
} from './StatelessWorkCardsUpsellModal.styles';

const FILTER_PANEL_STYLE: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
};

const FILTER_PANEL_SCROLL_STYLE: React.CSSProperties = {
  ...FILTER_PANEL_STYLE,
  overflowY: 'auto',
};

export interface ModalFiltersSectionProps {
  isMobile: boolean;
  filtersExpanded: boolean;
  onToggleFilters: (opening: boolean) => void;
  filterPanelImpressionSource: string;
  sourceWorkId: WorkId;
  modalVariant: string;
  isPremium: boolean;
  topicQuery: string;
  onTopicQueryChange: (value: string) => void;
  dateRange: DateRangeFilter;
  onDateRangeChange: (value: DateRangeFilter) => void;
  selectedBuckets: Set<string>;
  onSelectAllBuckets: () => void;
  onToggleBucket: (bucket: string) => void;
  availableBuckets: Set<string>;
  buildTrackClick: BuildPillTrackClick;
}

export function ModalFiltersSection({
  isMobile,
  filtersExpanded,
  onToggleFilters,
  filterPanelImpressionSource,
  sourceWorkId,
  modalVariant,
  isPremium,
  topicQuery,
  onTopicQueryChange,
  dateRange,
  onDateRangeChange,
  selectedBuckets,
  onSelectAllBuckets,
  onToggleBucket,
  availableBuckets,
  buildTrackClick,
}: ModalFiltersSectionProps) {
  return (
    <FiltersColumn className={filtersExpanded ? 'expanded' : 'collapsed'}>
      {(isMobile || filtersExpanded) && (
        <FilterToggle
          expanded={filtersExpanded}
          onToggle={onToggleFilters}
          mobileLayout={isMobile}
        />
      )}

      {isMobile && filtersExpanded && <MobileAccordionDivider />}

      {filtersExpanded && (
        <ImpressionWatcher
          source={filterPanelImpressionSource}
          entity_type={ImpressionEntity.Work}
          entity_id={sourceWorkId}
          extra={{
            modalVariant,
            is_premium: isPremium,
          }}
          style={isMobile ? FILTER_PANEL_STYLE : FILTER_PANEL_SCROLL_STYLE}
        >
          <FilterPanel
            topicQuery={topicQuery}
            dateRange={dateRange}
            selectedBuckets={selectedBuckets}
            availableBuckets={availableBuckets}
            onTopicQueryChange={onTopicQueryChange}
            onDateRangeChange={onDateRangeChange}
            onSelectAllBuckets={onSelectAllBuckets}
            onToggleBucket={onToggleBucket}
            buildTrackClick={buildTrackClick}
            showSaveButton={isMobile}
            onSaveFilters={() => onToggleFilters(false)}
            mobileLayout={isMobile}
          />
        </ImpressionWatcher>
      )}
    </FiltersColumn>
  );
}

export default ModalFiltersSection;
